import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../services/api';

const AuthContext = createContext(null);

// credentials: 'include' so the session cookie flows cross-origin in dev
// (:3000 -> :5000). In a same-origin/reverse-proxy deploy it's sent anyway.
async function authFetch(path, opts = {}) {
  const res = await fetch(apiUrl(path), {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  let data = null;
  try { data = await res.json(); } catch (_e) { /* no body */ }
  return { ok: res.ok, status: res.status, data };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('local');
  const [guestTtlDays, setGuestTtlDays] = useState(7);
  const [apiPaused, setApiPaused] = useState(false);
  const [apiPausedUntil, setApiPausedUntil] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const { ok, data } = await authFetch('/api/auth/me', { method: 'GET' });
    if (ok && data) {
      const mode = data.auth_mode || 'local';
      setAuthMode(mode);
      if (data.guest_ttl_days) setGuestTtlDays(data.guest_ttl_days);
      setApiPaused(!!data.api_paused);
      setApiPausedUntil(data.api_paused_until || null);
      if (!data.user && data.guest_enabled && (mode === 'oidc' || mode === 'proxy')) {
        // No real user yet, but guest mode is on — silently mint a guest sandbox so
        // the visitor can explore. If it fails, fall through to the sign-in wall.
        const res = await authFetch('/api/auth/guest', { method: 'POST' });
        setUser(res.ok && res.data ? res.data.user : null);
      } else {
        setUser(data.user || null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { ok, status, data } = await authFetch('/api/auth/login', {
      method: 'POST', body: JSON.stringify({ email, password }),
    });
    if (ok) { setUser(data.user); return { ok: true }; }
    return { ok: false, error: (data && data.error) || (status === 429 ? 'Too many attempts — try again later.' : 'Login failed') };
  }, []);

  const register = useCallback(async (email, password, display_name) => {
    const { ok, data } = await authFetch('/api/auth/register', {
      method: 'POST', body: JSON.stringify({ email, password, display_name }),
    });
    if (ok) { setUser(data.user); return { ok: true }; }
    return { ok: false, error: (data && data.error) || 'Registration failed' };
  }, []);

  const loginWithGoogle = useCallback(async (idToken) => {
    const { ok, status, data } = await authFetch('/api/auth/google', {
      method: 'POST', body: JSON.stringify({ id_token: idToken }),
    });
    if (ok) { setUser(data.user); return { ok: true }; }
    return { ok: false, error: (data && data.error) || (status === 429 ? 'Too many attempts — try again later.' : 'Google sign-in failed') };
  }, []);

  const logout = useCallback(async () => {
    await authFetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isGuest: !!(user && user.is_guest), isOwner: !!(user && user.is_owner), guestTtlDays, apiPaused, apiPausedUntil, authMode, loading, login, register, logout, loginWithGoogle, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
